import { CheckIcon } from "lucide-react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { type CalendarColorKey, calendarColors } from "../lib/calendar-colors.js";
import { cn } from "../lib/cn.js";

type Swatches<K extends string> = Readonly<Record<K, { name: string; swatch: string }>>;

export interface ColorSwatchPickerProps<K extends string = CalendarColorKey> {
  value: K;
  onValueChange: (value: K) => void;
  /** The colour set: calendar colours by default, or the ink colours (spec09). */
  colors?: Swatches<K>;
  id?: string;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

/**
 * Picks one colour of a fixed set (DESIGN.md: Colour swatch picker). Square 28px swatches,
 * the selected one ringed and checked. Radio-group semantics; each swatch is named.
 */
export function ColorSwatchPicker<K extends string = CalendarColorKey>({
  value,
  onValueChange,
  colors = calendarColors as unknown as Swatches<K>,
  id,
  className,
  ...aria
}: ColorSwatchPickerProps<K>) {
  const keys = Object.keys(colors) as K[];
  return (
    <RadioGroupPrimitive.Root
      id={id}
      value={value}
      onValueChange={(next) => onValueChange(next as K)}
      orientation="horizontal"
      className={cn("flex flex-wrap gap-2", className)}
      {...aria}
    >
      {keys.map((key) => (
        <RadioGroupPrimitive.Item
          key={key}
          value={key}
          aria-label={colors[key].name}
          title={colors[key].name}
          className={cn(
            "touch-target relative flex size-7 items-center justify-center border border-input text-background outline-offset-2 transition-[outline-color] duration-150 focus-visible:outline-2 focus-visible:outline-ring data-[state=checked]:outline-2 data-[state=checked]:outline-foreground",
            colors[key].swatch,
          )}
        >
          <RadioGroupPrimitive.Indicator>
            <CheckIcon className="size-3.5" strokeWidth={3} aria-hidden />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
